import { useEffect, useRef, useState } from "react";

/**
 * Guarda cada campo apenas la persona lo deja, para que no dependa de acordarse
 * de un boton.
 *
 * Tres reglas que vienen del problema que se quiere evitar:
 *
 * - Solo escribe si el valor cambio respecto a lo ultimo que confirmo el
 *   servidor. Salir de un campo sin tocarlo no manda nada.
 * - Una sola peticion en vuelo por campo. Si llega una respuesta vieja despues
 *   de una nueva, se descarta, para que la lenta no pise a la ultima.
 * - Un fallo no borra lo escrito ni mueve a la persona de sitio: deja el aviso
 *   y la posibilidad de reintentar.
 */

export type EstadoGuardado = "inactivo" | "guardando" | "guardado" | "error";

export type ResultadoGuardado =
  | "guardado"
  | "sin-cambios"
  | "error"
  | "descartado";

/** Una foto del valor, para comparar arreglos y objetos y no solo textos. */
const instantanea = (valor: any): string => JSON.stringify(valor ?? null);

/** El texto que acompaña al indicador. */
export const textoGuardado = (
  estado: EstadoGuardado,
  guardadoEn: Date | null
): string => {
  if (estado === "guardando") return "Guardando…";
  if (estado === "error") return "No se pudo guardar";
  if (estado === "guardado" && guardadoEn) {
    const dos = (n: number) => `0${n}`.slice(-2);
    return `Guardado ${dos(guardadoEn.getHours())}:${dos(guardadoEn.getMinutes())}`;
  }
  return "";
};

export const useAutoguardado = () => {
  const [estado, setEstado] = useState<EstadoGuardado>("inactivo");
  const [guardadoEn, setGuardadoEn] = useState<Date | null>(null);

  /** Lo ultimo que el servidor confirmo para cada campo. */
  const confirmado = useRef<{ [clave: string]: string }>({});
  /** El numero de la peticion vigente de cada campo. */
  const vigente = useRef<{ [clave: string]: number }>({});
  /** El ultimo intento fallido, para poder reintentarlo. */
  const fallido = useRef<null | { clave: string; valor: any; escribir: () => Promise<any> }>(null);
  const montado = useRef(true);

  useEffect(
    () => () => {
      montado.current = false;
    },
    []
  );

  /**
   * Fija el valor que ya tiene el servidor, sin escribir nada. Se llama al
   * cargar la postulacion para que el primer blur no mande lo que acaba de
   * llegar.
   */
  const sembrar = (clave: string, valor: any) => {
    confirmado.current[clave] = instantanea(valor);
  };

  const guardar = async (
    clave: string,
    valor: any,
    escribir: () => Promise<any>
  ): Promise<ResultadoGuardado> => {
    const foto = instantanea(valor);
    if (confirmado.current[clave] === foto) return "sin-cambios";

    const numero = (vigente.current[clave] ?? 0) + 1;
    vigente.current[clave] = numero;
    setEstado("guardando");

    try {
      await escribir();
      if (!montado.current || vigente.current[clave] !== numero) return "descartado";

      confirmado.current[clave] = foto;
      fallido.current = null;
      setEstado("guardado");
      setGuardadoEn(new Date());
      return "guardado";
    } catch (error) {
      if (!montado.current || vigente.current[clave] !== numero) return "descartado";

      console.error(`No se pudo guardar ${clave}`, error);
      fallido.current = { clave, valor, escribir };
      setEstado("error");
      return "error";
    }
  };

  const hayQueReintentar = () => fallido.current !== null;

  const reintentar = async (): Promise<ResultadoGuardado> => {
    const intento = fallido.current;
    if (!intento) return "sin-cambios";
    // Se vuelve a intentar el mismo valor, no uno nuevo.
    return guardar(intento.clave, intento.valor, intento.escribir);
  };

  return {
    estado,
    guardadoEn,
    texto: textoGuardado(estado, guardadoEn),
    sembrar,
    guardar,
    reintentar,
    hayQueReintentar,
  };
};
